import React from "react";
import { View, StyleSheet } from "react-native";
import { CaretLeftIcon as CaretLeftBase, CaretRightIcon as CaretRightBase } from "@phosphor-icons/react";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";
import { GradingCenterModalState } from "../types";
import { useNavigation } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { RootStackParamList } from "../../../../navigation/navigation.types";
import { colors, spacing, radii, webTransitions } from "../../../../styles/design-system";

type AdminGradingCenterNavProp = StackNavigationProp<RootStackParamList, "Admin Grading Center">;

interface StudentNavigationPanelProps {
  setModalState: (state: GradingCenterModalState) => void;
}

const getModalStateForType = (type: string): GradingCenterModalState => {
  if (type === "Assessment") {
    return GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE;
  }
  if (type === "Project") {
    return GradingCenterModalState.VIEW_PROJECT_RESPONSE;
  }
  return GradingCenterModalState.VIEW_ASSIGNMENT_RESPONSE;
};

const CaretLeftIcon = CaretLeftBase as unknown as React.ComponentType<{ size?: number; color?: string }>;
const CaretRightIcon = CaretRightBase as unknown as React.ComponentType<{ size?: number; color?: string }>;

const StudentNavigationPanel: React.FC<StudentNavigationPanelProps> = ({ setModalState }) => {
  const { filteredSubmissions, selectedSubmissionData, setSelectedSubmissionData } = useGradingCenterSubmissions();

  const sameTypeSubmissions = filteredSubmissions.filter(
    (s) =>
      s.submissionType === selectedSubmissionData?.submissionType &&
      s.submissionTitle === selectedSubmissionData?.submissionTitle
  );

  const navigation = useNavigation<AdminGradingCenterNavProp>();

  const currentIndex = sameTypeSubmissions.findIndex((s) => s.id === selectedSubmissionData?.id);
  const total = sameTypeSubmissions.length;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < total - 1;

  const navigate = (index: number) => {
    const submission = sameTypeSubmissions[index];
    setSelectedSubmissionData(submission);
    setModalState(getModalStateForType(submission.submissionType));
    navigation.setParams({ submissionId: submission.id });
  };

  return (
    <View style={styles.container}>
      <CustomButton
        text="Previous"
        leftIcon={<CaretLeftIcon size={16} color={hasPrevious ? colors.grey[700] : colors.grey[300]} />}
        rightIcon={null}
        size="s"
        styleType="transparent"
        disabled={!hasPrevious}
        onPress={() => navigate(currentIndex - 1)}
        style={styles.navButton}
        textStyle={hasPrevious ? styles.navText : styles.navTextDisabled}
      />

      <CustomText
        text={currentIndex >= 0 ? `${currentIndex + 1} of ${total}` : ""}
        size="xs"
        style={styles.counter}
        useTranslationText={false}
      />

      <CustomButton
        text="Next"
        leftIcon={null}
        rightIcon={<CaretRightIcon size={16} color={hasNext ? colors.grey[700] : colors.grey[300]} />}
        size="s"
        styleType="transparent"
        disabled={!hasNext}
        onPress={() => navigate(currentIndex + 1)}
        style={styles.navButton}
        textStyle={hasNext ? styles.navText : styles.navTextDisabled}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  navButton: {
    borderRadius: radii.sm,
    ...webTransitions.fast,
  },
  navText: {
    color: colors.grey[700],
  },
  navTextDisabled: {
    color: colors.grey[300],
  },
  counter: {
    color: colors.grey[500],
  },
});

export default StudentNavigationPanel;
